import {BSKY_LABELER_DID, type Did} from '@atproto/api'

import packageJson from '#/../package.json'

/**
 * The semver version of the app, as defined in `package.json.`
 *
 * N.B. The fallback is needed for Render.com deployments
 */
export const RELEASE_VERSION: string =
  process.env.EXPO_PUBLIC_RELEASE_VERSION || packageJson.version

/**
 * The env the app is running in e.g. development, testflight, production, e2e
 */
export const ENV: string = process.env.EXPO_PUBLIC_ENV as
  | 'production'
  | 'testflight'
  | 'development'
  | 'e2e'
  | (string & {})

/**
 * Indicates whether the app is running in TestFlight
 */
export const IS_TESTFLIGHT = ENV === 'testflight'

/**
 * Indicates whether the app is `__DEV__`
 */
export const IS_DEV = process.env.IS_DEV_MODE === 'true' ? true : __DEV__

/**
 * Indicates whether the app is running in a test environment
 */
export const IS_E2E = ENV === 'e2e'

/**
 * Indicates whether the app is `__DEV__` or TestFlight
 */
export const IS_INTERNAL = IS_DEV || IS_TESTFLIGHT

/**
 * The commit hash that the current bundle was made from. The user can
 * see the commit hash in the app's settings along with the other version info.
 * Useful for debugging/reporting.
 */
export const BUNDLE_IDENTIFIER: string =
  process.env.EXPO_PUBLIC_BUNDLE_IDENTIFIER || 'dev'

/**
 * This will always be in the format of YYMMDDHH, so that it always increases
 * for each build. This should only be used for analytics reporting and shouldn't
 * be used to identify a specific bundle.
 */
export const BUNDLE_DATE: number =
  process.env.EXPO_PUBLIC_BUNDLE_DATE === undefined
    ? 0
    : Number(process.env.EXPO_PUBLIC_BUNDLE_DATE)

/**
 * The log level for the app.
 */
export const LOG_LEVEL = (process.env.EXPO_PUBLIC_LOG_LEVEL || 'info') as
  | 'debug'
  | 'info'
  | 'warn'
  | 'error'

/**
 * Enable debug logs for specific logger instances
 */
export const LOG_DEBUG: string = process.env.EXPO_PUBLIC_LOG_DEBUG || ''

/**
 * The DID of the Bluesky appview to proxy to
 */
export const BLUESKY_PROXY_DID: Did =
  process.env.EXPO_PUBLIC_BLUESKY_PROXY_DID || 'did:web:api.bsky.app'

/**
 * The DID of the chat service to proxy to
 */
export const CHAT_PROXY_DID: Did =
  process.env.EXPO_PUBLIC_CHAT_PROXY_DID || 'did:web:api.bsky.chat'

/**
 * The DID of the default app labeler / moderation service. Defaults to
 * Bluesky's labeler; override to use our own Ozone instance.
 */
export const APP_LABELER_DID: Did =
  (process.env.EXPO_PUBLIC_APP_LABELER_DID as Did) || BSKY_LABELER_DID

/**
 * Metrics API host
 */
export const METRICS_API_HOST: string =
  process.env.EXPO_PUBLIC_METRICS_API_HOST || 'https://events.bsky.app'

/**
 * Growthbook API host
 */
export const GROWTHBOOK_API_HOST: string =
  process.env.EXPO_PUBLIC_GROWTHBOOK_API_HOST || `${METRICS_API_HOST}/gb`

/**
 * Growthbook client key
 */
export const GROWTHBOOK_CLIENT_KEY: string =
  process.env.EXPO_PUBLIC_GROWTHBOOK_CLIENT_KEY || 'sdk-7gkUkGy9wguUjyFe'

/**
 * Sentry DSN for telemetry
 */
export const SENTRY_DSN: string | undefined = process.env.EXPO_PUBLIC_SENTRY_DSN

/**
 * Bitdrift API key. If undefined, Bitdrift should be disabled.
 */
export const BITDRIFT_API_KEY: string | undefined =
  process.env.EXPO_PUBLIC_BITDRIFT_API_KEY

/**
 * GCP project ID which is required for native device attestation. On web, this
 * should be unset and evaluate to 0.
 */
export const GCP_PROJECT_ID: number =
  process.env.EXPO_PUBLIC_GCP_PROJECT_ID === undefined
    ? 0
    : Number(process.env.EXPO_PUBLIC_GCP_PROJECT_ID)

/**
 * URLs for the app config web worker. Can be a
 * locally running server, see `env.example` for more.
 */
export const BAPP_CONFIG_DEV_URL = process.env.BAPP_CONFIG_DEV_URL
export const BAPP_CONFIG_PROD_URL =
  process.env.APP_API_URL ?? `https://pds-dev.wsocial.dev`
export const BAPP_CONFIG_URL = IS_DEV
  ? (BAPP_CONFIG_DEV_URL ?? BAPP_CONFIG_PROD_URL)
  : BAPP_CONFIG_PROD_URL
export const GEOLOCATION_DEV_URL = process.env.GEOLOCATION_DEV_URL
export const GEOLOCATION_PROD_URL = `https://ip.bsky.app`
export const GEOLOCATION_URL = IS_DEV
  ? (GEOLOCATION_DEV_URL ?? GEOLOCATION_PROD_URL)
  : GEOLOCATION_PROD_URL

/**
 * URLs for the live-event config web worker. Can be a
 * locally running server, see `env.example` for more.
 */
export const LIVE_EVENTS_DEV_URL = process.env.LIVE_EVENTS_DEV_URL
export const LIVE_EVENTS_PROD_URL = process.env.EXPO_PUBLIC_LIVE_EVENTS_URL
export const LIVE_EVENTS_URL = IS_DEV
  ? (LIVE_EVENTS_DEV_URL ?? LIVE_EVENTS_PROD_URL)
  : LIVE_EVENTS_PROD_URL

export const ANNOUNCEMENTS_URL = process.env.EXPO_PUBLIC_ANNOUNCEMENTS_URL

/**
 * URLs for the app-config web worker. Can be a
 * locally running server, see `env.example` for more.
 * If unset, no request is made and DEFAULT_APP_CONFIG_RESPONSE is used.
 */
export const APP_CONFIG_DEV_URL = process.env.APP_CONFIG_DEV_URL
export const APP_CONFIG_PROD_URL = process.env.EXPO_PUBLIC_APP_CONFIG_URL
export const APP_CONFIG_URL = IS_DEV
  ? (APP_CONFIG_DEV_URL ?? APP_CONFIG_PROD_URL)
  : APP_CONFIG_PROD_URL

export const GEOLOCATION_API_URL = process.env.EXPO_PUBLIC_GEOLOCATION_API_URL

/**
 * Base URL for the W Trends service
 * (`GET /xrpc/eu.wsocial.unspecced.getExplore`). Defaults to the W AppView
 * trends host; override per environment as needed.
 */
export const WTRENDS_API_URL: string =
  process.env.EXPO_PUBLIC_WTRENDS_API_URL || 'https://trend.wsocial.cloud'

/**
 * Path of the W Trends explore endpoint, joined onto `WTRENDS_API_URL`.
 * Defaults to the AT Protocol XRPC route; override per environment if the
 * backend moves it. Must start with a leading slash so it resolves against the
 * host root (see `new URL(path, base)` in `fetchTrends.ts`).
 */
export const WTRENDS_API_PATH: string =
  process.env.EXPO_PUBLIC_WTRENDS_API_PATH ||
  '/xrpc/eu.wsocial.unspecced.getExplore'

/**
 * Bearer token for the W Trends service. Read-only during development. When
 * unset, the Explore feed cannot load live trends and surfaces an error state
 * (there is intentionally no mock fallback — see `useTrends`).
 */
export const WTRENDS_API_TOKEN: string | undefined =
  process.env.EXPO_PUBLIC_WTRENDS_API_TOKEN || undefined

/**
 * Whether the W Trends Explore feature is configured for this build. False when
 * the API URL or token is missing — in that case `/search` falls back to the
 * upstream Bluesky Explore instead of the W Social trends UI.
 */
export const WTRENDS_ENABLED: boolean = Boolean(
  WTRENDS_API_URL && WTRENDS_API_TOKEN,
)

/**
 * Base URL for the W Social translation service (path included).
 * Defaults to production; override per environment as needed.
 */
export const TRANSLATION_SERVICE_URL: string =
  process.env.EXPO_PUBLIC_TRANSLATION_SERVICE_URL ||
  'https://tools.wsocial.network/translations'

/**
 * Comma-separated list of DIDs allowed to see the content-source switcher,
 * regardless of the server-side `contentSourceEnabled` flag. Used to gate the
 * feature to specific testers without rolling it out to everyone.
 *
 * When empty (the default), the switcher is shown to all users whenever the
 * server reports `contentSourceEnabled = true`.
 *
 * Example: EXPO_PUBLIC_CONTENT_SOURCE_ALLOWED_DIDS=did:plc:abc123,did:plc:def456
 */
export const CONTENT_SOURCE_ALLOWED_DIDS: readonly string[] = String(
  process.env.EXPO_PUBLIC_CONTENT_SOURCE_ALLOWED_DIDS ?? '',
)
  .split(',')
  .map(s => s.trim())
  .filter(Boolean)
